import { useCMSPageField } from '@/cms/fields'
import { createAvailabilityApiClient } from '@/infrastructure/prices/availability/availabilityApiClient'
import { createFiltersApiClient } from '@/infrastructure/prices/filters/api/filtersAPIClient'
import { createPricesApiClient } from '@/infrastructure/prices/pricesByDuration/api/pricesAPIClient'
import { createPricesFiltersParser } from '@/infrastructure/prices/pricesFiltersParser'
import { createPromotedPriceApiClient } from '@/infrastructure/prices/promoted-price/promotedPriceApiClient'
import { createRoomSelectorApiClient } from '@/infrastructure/prices/rooms/api/roomSelectorApiClient'
import { createFlightSelectorApiClient } from '@/infrastructure/prices/transport/api/flight/flightSelectorApiClient'
import { createSelfDriveSelectorApiClient } from '@/infrastructure/prices/transport/api/selfdrive/selfDriveSelectorApiClient'
import { useAccommodationContext } from '@/react-pages/Prices/AccommodationContext'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { getPromotedPriceSelector, isAccommodationAvailableSelector } from '@/scopes/prices/components/selectors'
import { pricesUseCases } from '@/scopes/prices/domain/useCases/pricesUseCases'
import { newPricesStore } from '@/scopes/prices/infrastructure/store'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import * as React from 'react'
import { PricesUseCasesContext } from './context'

interface PricesUseCasesContextProviderProps {
  enabled: boolean
  children: React.ReactNode
}

const { initPrices, initPriceTable, selectFilter, selectPrice, selectRoom, setOutboundRouteId, setInboundRouteId } = pricesUseCases({
  filtersApiClient: createFiltersApiClient({}),
  pricesApiClient: createPricesApiClient({}),
  roomSelectorApiClient: createRoomSelectorApiClient({}),
  flightSelectorApiClient: createFlightSelectorApiClient({}),
  selfDriveSelectorApiClient: createSelfDriveSelectorApiClient({}),
  analyticsClient: ga4analyticsClient,
  store: newPricesStore,
  queryStringSerializer: createQueryStringSerializer({
    parser: createPricesFiltersParser(),
  }),
  queryParametersToStateMapper: {
    toState: queryParameters => ({
      filterSelection: queryParameters,
    }),
    toParameters: ({ filterSelection }) => filterSelection,
  },
  browserQueryString: createBrowserQueryStringKeepingParams({}),
  promotedPriceAPIClient: createPromotedPriceApiClient({}),
  isAccoAvailableApiClient: createAvailabilityApiClient({}),
})

export function PricesUseCasesContextProvider({ enabled, children }: PricesUseCasesContextProviderProps) {
  const { contextItemId, accoId, bookingGateId, promotedPriceId } = useAccommodationContext()
  const { success: promotedSuccess } = usePricesSelector(getPromotedPriceSelector)
  const { success: availabilitySuccess } = usePricesSelector(isAccommodationAvailableSelector)
  const queryStringClient = useCommonDependency('queryStringClient')
  const analytics = useCMSPageField('PDP.pricetable.analytics')
  const initialize = React.useCallback(async () => {
    if (enabled) {
      const originList = queryStringClient.getValue('originList')
      await initPrices({
        accoId,
        bookingGateId,
        contextItemId,
        analytics: analytics ? { ...analytics, originList: originList || undefined } : null,
        initialFilterSelection: {
          Participants: [
            {
              adults: 2,
              children: [],
              babies: [],
            },
          ],
        },
        promotedPriceId,
      })
    }
  }, [accoId, bookingGateId, contextItemId])

  React.useEffect(() => {
    (async () => await initialize())()
  }, [])

  React.useLayoutEffect(() => {
    if (availabilitySuccess && promotedSuccess) {
      const url = window.location.search
      const params = new URLSearchParams(url)
      const showPriceTableParam = params.get('focusPriceTable')
      if (showPriceTableParam && showPriceTableParam?.toLowerCase() === 'true') {
        initPriceTable()
      }
    }
  }, [availabilitySuccess, promotedSuccess])

  return (
    <PricesUseCasesContext.Provider
      value={{
        initPrices,
        initPriceTable,
        selectFilter,
        selectPrice,
        selectRoom,
        setOutboundRouteId,
        setInboundRouteId,
      }}
    >
      {children}
    </PricesUseCasesContext.Provider>
  )
}
